import * as React from 'react';
import Button from '@mui/material/Button';
import { styled, alpha } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { Box, Typography } from '@mui/material';

import { DialogProps } from './types';

// ─── Brand ───────────────────────────────────────────────────────────────
// Matches "Design 1" — the reference popup: a navy header (two-stop
// gradient, no glow), a 4px brand-green accent stripe along its bottom
// edge, and a flat (non-gradient) green primary action below.
//
// NOTE: kept as a two-stop gradient (no third "…_DARK" stop, no radial
// glow overlay) intentionally — that extra layer is what caused the
// previous header to render as a flat opaque green instead of the
// intended navy. Simpler chrome here is deliberate, not an omission.
const NAVY_900 = '#0A1A2F';
const NAVY_800 = '#0E2340';
const ACCENT_GREEN = '#0F9D58';
const ACCENT_GREEN_LIGHT = '#2FD47E';
const ACCENT_GREEN_DARK = '#0C7D46';
const LINE = '#E6EBF1';

// ─── Motion ────────────────────────────────────────────────────────────────
// A short upward slide + fade reads as more considered than the default
// pop-in, without drawing attention to itself. Unchanged from before —
// swapping this out is a bigger behavioral change than a chrome update
// warrants across every form that uses this dialog.
const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} timeout={220} {...props} />;
});

// ─── Chrome ──────────────────────────────────────────────────────────────
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: alpha('#0A1423', 0.55),
    backdropFilter: 'blur(2px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(10, 20, 40, 0.35)',
    overflow: 'hidden',
    backgroundImage: 'none',
    margin: theme.spacing(2),
    // Only cap on phones — on desktop this override was defeating the
    // maxWidth prop (xs/sm/...) and stretching every dialog full-width.
    // phones: bottom sheet, full width (see MuiDialog in design-system/theme.ts)
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      maxWidth: '100%',
      width: '100%',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 3, 3, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
}));

export default function PopupDialog(props: DialogProps) {
  const {
    children,
    header,
    onReleaseClick,
    isOpen,
    setOpen,
    releaseButtonLabel,
    showButton = false,
    onButtonClick,
    showCancelButton = true,
    cancelButtonLabel = 'Cancel',
    cancelButtonDisabled = false,
    buttonDisabled = false,
    keepOpenOnAction = false,
    maxWidth = 'sm',
    contentSx,
  } = props;

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
    if (!keepOpenOnAction && onReleaseClick) {
      onReleaseClick();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="popup-dialog-title"
      open={isOpen}
      maxWidth={maxWidth}
      fullWidth
      TransitionComponent={SlideUpTransition}
      keepMounted={false}
    >
      {header && (
        <Box
          sx={{
            position: 'relative',
            px: { xs: 2, sm: 3 },
            pt: { xs: 1.75, sm: 2.25 },
            pb: { xs: 2, sm: 2.5 },
            background: `linear-gradient(135deg, ${NAVY_900} 0%, ${NAVY_800} 100%)`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          {/* Brand accent — a 4px green stripe along the bottom edge of
              the header, echoing Design 1 exactly. This replaces the
              previous radial glow, which is intentionally dropped. */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${ACCENT_GREEN} 0%, ${ACCENT_GREEN_LIGHT} 100%)`,
              pointerEvents: 'none',
            }}
          />

          <Typography
            id="popup-dialog-title"
            component="div"
            sx={{
              flex: 1,
              position: 'relative',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.9375rem', sm: '1.0625rem' },
              fontWeight: 700,
              lineHeight: 1.35,
              letterSpacing: '-0.01em',
              color: '#ffffff',
              wordBreak: 'break-word',
            }}
          >
            {header}
          </Typography>

          <IconButton
            aria-label="Close dialog"
            onClick={handleClose}
            sx={{
              flexShrink: 0,
              color: '#C3CEDE',
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                color: '#ffffff',
              },
              '&:focus-visible': {
                outline: '2px solid rgba(255,255,255,0.9)',
                outlineOffset: '2px',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '0.9375rem' }} />
          </IconButton>
        </Box>
      )}

      <DialogContent sx={{ pt: 3, ...contentSx }}>{children}</DialogContent>

      {showButton && (
        <DialogActions
          sx={{
            p: 2.5,
            gap: 1,
            borderTop: `1px solid ${LINE}`,
          }}
        >
          {showCancelButton && (
            <Button
              onClick={onReleaseClick}
              disableRipple
              disabled={cancelButtonDisabled}
              variant="cancelAction"
            >
              {cancelButtonLabel}
            </Button>
          )}
          <Button
            onClick={handleButtonClick}
            disableRipple
            disabled={buttonDisabled}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.84375rem',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: ACCENT_GREEN,
              borderRadius: '10px',
              px: 2.5,
              py: 1.25,
              textTransform: 'none',
              border: 'none',
              boxShadow: `0 6px 16px ${alpha(ACCENT_GREEN, 0.3)}`,
              transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                backgroundColor: ACCENT_GREEN_DARK,
                boxShadow: `0 8px 20px ${alpha(ACCENT_GREEN, 0.38)}`,
              },
              '&:active': {
                transform: 'scale(0.97)',
              },
            }}
          >
            {releaseButtonLabel}
          </Button>
        </DialogActions>
      )}
    </BootstrapDialog>
  );
}
